// Freedom Journey — the lifecycle (F-01) dashboard. Shows the Freedom Meter, the
// Asset vs Liability classifier, the player's portfolio, net worth, cashflow
// quadrant and mindset — all from the localStorage-backed lifecycle store.

import { motion } from 'framer-motion';
import type { Quadrant } from '@biz-kid/engine';
import { ThemeToggle } from '../ui/theme.js';
import { Card, Eyebrow, Button, Chip, money } from '../ui/kit.js';
import { mastermindUnlocked } from '@biz-kid/engine';
import { useLife, useLifeComputed, useLifeDispatch } from './store.js';
import { FreedomMeter } from './FreedomMeter.js';
import { AssetLiabilityClassifier } from './AssetLiabilityClassifier.js';

const QUADRANTS: { key: Quadrant; label: string; sub: string }[] = [
  { key: 'E', label: 'E', sub: 'Employee' },
  { key: 'S', label: 'S', sub: 'Self-employed' },
  { key: 'B', label: 'B', sub: 'Business' },
  { key: 'I', label: 'I', sub: 'Investor' },
];

export function LifeScreen({ onClose, onVision, onMastermind }: { onClose: () => void; onVision?: () => void; onMastermind?: () => void }) {
  const s = useLife();
  const c = useLifeComputed();
  const dispatch = useLifeDispatch();
  const showMastermind = mastermindUnlocked(s.age);

  return (
    <div className="bk-app" style={{ position: 'fixed', inset: 0, zIndex: 9, overflowY: 'auto' }}>
      <div style={{ maxWidth: 760, margin: '0 auto', padding: 'clamp(14px, 3vw, 26px)', paddingBottom: 90 }}>
        <header style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <div>
            <Eyebrow color="var(--brand)">YOUR EMPIRE</Eyebrow>
            <h1 style={{ margin: '2px 0 0', fontSize: 22, fontWeight: 900, color: 'var(--text)' }}>
              AGE {s.age} · EMPIRE VALUE {money(c.netWorth)}
            </h1>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            {showMastermind && onMastermind && <Button tone="soft" size="sm" onClick={onMastermind}>🧠 YOUR CREW</Button>}
            {onVision && <Button tone="soft" size="sm" onClick={onVision}>✨ DREAM WALL</Button>}
            <ThemeToggle />
            <Button tone="ghost" size="sm" onClick={onClose}>← HOME</Button>
          </div>
        </header>

        {/* the meter — front and centre */}
        <div style={{ marginBottom: 14 }}>
          <FreedomMeter
            passiveIncome={c.passiveIncome}
            monthlyExpenses={c.monthlyExpenses}
            freedomPct={c.freedomPct}
            freedomMode={c.freedomMode}
          />
        </div>

        {/* quadrant + cash */}
        <Card style={{ marginBottom: 14 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 10 }}>
            <Eyebrow>YOUR LANE</Eyebrow>
            <Chip tone="brand">{money(s.cash)} CASH</Chip>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
            {QUADRANTS.map((q) => {
              const active = s.quadrant === q.key;
              return (
                <div
                  key={q.key}
                  style={{
                    padding: '10px 6px',
                    borderRadius: 12,
                    textAlign: 'center',
                    background: active ? 'var(--brand)' : 'var(--surface-2)',
                    color: active ? 'var(--brand-ink)' : 'var(--text-2)',
                    border: `1px solid ${active ? 'var(--brand)' : 'var(--border)'}`,
                  }}
                >
                  <div style={{ fontSize: 20, fontWeight: 900 }}>{q.label}</div>
                  <div style={{ fontSize: 10.5, fontWeight: 700 }}>{q.sub}</div>
                </div>
              );
            })}
          </div>
          <p style={{ margin: '10px 0 0', fontSize: 13, color: 'var(--text-2)', lineHeight: 1.5, fontWeight: 700 }}>
            GOAL: make money work for YOU.
          </p>
        </Card>

        {/* the classifier */}
        <div style={{ marginBottom: 14 }}>
          <AssetLiabilityClassifier />
        </div>

        {/* portfolio */}
        <Card style={{ marginBottom: 14 }}>
          <Eyebrow color="var(--up)">MONEY MACHINES + DRAINS</Eyebrow>
          <div style={{ display: 'grid', gap: 8, marginTop: 10 }}>
            {s.assets.length === 0 && s.liabilities.length === 0 && (
              <p style={{ fontSize: 13, color: 'var(--text-3)', margin: 0, fontWeight: 700 }}>
                NOTHING YET. GO BUILD MONEY MACHINES.
              </p>
            )}
            {s.assets.map((a) => (
              <Row key={a.id} icon="📈" name={a.name} note={`worth ${money(a.value)}`} amount={`+${money(a.monthlyIncome)}/mo`} tone="up" onRemove={() => dispatch({ type: 'REMOVE_ASSET', id: a.id })} />
            ))}
            {s.liabilities.map((l) => (
              <Row key={l.id} icon="📉" name={l.name} note={`owe ${money(l.value)}`} amount={`−${money(l.monthlyCost)}/mo`} tone="down" onRemove={() => dispatch({ type: 'REMOVE_LIABILITY', id: l.id })} />
            ))}
          </div>
        </Card>

        {/* mindset */}
        <Card>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
            <Eyebrow color="var(--brand)">YOUR MINDSET</Eyebrow>
            <span style={{ fontSize: 12, fontWeight: 800, color: 'var(--text-2)' }}>AVG {c.mindsetAverage}</span>
          </div>
          <div style={{ display: 'grid', gap: 8, marginTop: 12 }}>
            {(['desire', 'faith', 'persistence', 'focus', 'gratitude'] as const).map((k) => (
              <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ width: 90, fontSize: 12.5, color: 'var(--text-2)', fontWeight: 700, textTransform: 'capitalize' }}>{k}</span>
                <div style={{ flex: 1, height: 8, borderRadius: 999, background: 'var(--surface-3)', overflow: 'hidden' }}>
                  <div style={{ width: `${s.mindset[k]}%`, height: '100%', background: 'var(--brand)', borderRadius: 999 }} />
                </div>
                <span className="bk-tnum" style={{ width: 28, textAlign: 'right', fontSize: 12, fontWeight: 800, color: 'var(--text)' }}>{s.mindset[k]}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}

function Row({ icon, name, note, amount, tone, onRemove }: { icon: string; name: string; note: string; amount: string; tone: 'up' | 'down'; onRemove: () => void }) {
  return (
    <motion.div
      layout
      style={{ display: 'flex', alignItems: 'center', gap: 10, padding: 10, borderRadius: 12, background: 'var(--surface-2)', border: '1px solid var(--border)' }}
    >
      <span style={{ fontSize: 20 }}>{icon}</span>
      <span style={{ flex: 1 }}>
        <span style={{ display: 'block', fontWeight: 800, fontSize: 14, color: 'var(--text)' }}>{name}</span>
        <span style={{ display: 'block', fontSize: 11.5, color: 'var(--text-3)' }}>{note}</span>
      </span>
      <span className="bk-tnum" style={{ fontWeight: 900, fontSize: 14, color: tone === 'up' ? 'var(--up)' : 'var(--down)' }}>{amount}</span>
      <button type="button" onClick={onRemove} aria-label="Remove" style={{ border: 'none', background: 'transparent', color: 'var(--text-3)', cursor: 'pointer', fontSize: 16, padding: 4 }}>×</button>
    </motion.div>
  );
}
